import { supabase } from "@/lib/supabase";
import type { BotFunnelRow, BotRule } from "@/lib/bot-funnel";
import type { UrlActiveFilters } from "@/lib/url-active-filters";

type Result<T> = PromiseLike<{ data: T | null; error: { message: string } | null }>;

/**
 * bot_rules and get_bot_funnel (20261019170000) are newer than
 * src/types/database.ts: a structural view of the client until the types are
 * regenerated - no `any`, no @ts-expect-error to rot.
 */
interface BotClient {
  from(table: "bot_rules"): {
    select(columns: string): { order(column: string): Result<BotRule[]> };
    update(values: Pick<BotRule, "enabled" | "patterns">): {
      eq(column: string, value: string): { select(columns: string): Result<{ key: string }[]> };
    };
  };
  rpc(
    fn: "get_bot_funnel",
    args: { p_start_date?: string; p_end_date: string; p_filters?: UrlActiveFilters },
  ): Result<BotFunnelRow[]>;
}

const db = supabase as unknown as BotClient;

export const BotDetectionService = {
  async getRules(): Promise<BotRule[]> {
    const { data, error } = await db.from("bot_rules").select("*").order("sort");
    if (error) throw new Error(error.message);
    return data ?? [];
  },

  /**
   * Rows the period's clicks fall into, one per bot bits, with totals - under
   * the Clicks filters if given (the server drops the bot filter itself).
   * No start date: All time, as the grid sends it.
   */
  async getFunnel(from: string | null, to: string, filters: UrlActiveFilters = {}): Promise<BotFunnelRow[]> {
    const { data, error } = await db.rpc("get_bot_funnel", {
      p_start_date: from ?? undefined,
      p_end_date: to,
      // Only when there are any: without them the call also fits the two-argument
      // version, so Settings keeps working where 20261022100000 is not out yet.
      ...(Object.keys(filters).length ? { p_filters: filters } : {}),
    });
    if (error) throw new Error(error.message);
    return (data ?? []).map((r) => ({
      ...r,
      clicks: Number(r.clicks),
      unique_global: Number(r.unique_global),
      conversions: Number(r.conversions),
      sales: Number(r.sales),
      revenue: Number(r.revenue),
    }));
  },

  async saveRule(key: string, values: Pick<BotRule, "enabled" | "patterns">): Promise<void> {
    const { data, error } = await db.from("bot_rules").update(values).eq("key", key).select("key");
    if (error) throw new Error(error.message);
    // Row-level security turns a non-admin's update into "0 rows", not an error.
    if (!data?.length) throw new Error("Only admins can change bot rules");
  },
};
